 
/**
 * Document Numbering Service
 *
 * API service for managing document numbering series
 */

import type {
  NumberingSeries, ResetFrequency, InvoiceType,
  NumberingAssignment, UpsertNumberingAssignmentRequest,
  QuickSetupRoleNumberingRequest, QuickSetupRoleNumberingResponse,
} from '../types/billing'

/* v8 ignore next */
const API_BASE = import.meta.env.VITE_API_URL ?? 'http://localhost:11002'

// Helper functions to convert API enum values to frontend types
function convertInvoiceType(apiType: string): InvoiceType {
  const typeMap: Record<string, InvoiceType> = {
    'INVOICE_TYPE_B2B': 'b2b',
    'INVOICE_TYPE_EXPORT': 'export',
    'INVOICE_TYPE_B2C': 'b2c',
    'INVOICE_TYPE_CREDIT_NOTE': 'credit_note',
    'INVOICE_TYPE_DEBIT_NOTE': 'debit_note',
    'INVOICE_TYPE_VENDOR_BILL': 'vendor_bill',
    'INVOICE_TYPE_VENDOR_CREDIT_NOTE': 'vendor_credit_note',
  }
  return typeMap[apiType] ?? 'b2b'
}

function convertResetFrequency(apiFreq: string): ResetFrequency {
  const freqMap: Record<string, ResetFrequency> = {
    'RESET_FREQUENCY_NEVER': 'never',
    'RESET_FREQUENCY_MONTHLY': 'monthly',
    'RESET_FREQUENCY_QUARTERLY': 'quarterly',
    'RESET_FREQUENCY_YEARLY': 'yearly',
  }
  return freqMap[apiFreq] ?? 'yearly'
}


function convertToApiType(frontendType: InvoiceType): string {
  const typeMap: Record<InvoiceType, string> = {
    'b2b': 'INVOICE_TYPE_B2B',
    'export': 'INVOICE_TYPE_EXPORT',
    'b2c': 'INVOICE_TYPE_B2C',
    'credit_note': 'INVOICE_TYPE_CREDIT_NOTE',
    'debit_note': 'INVOICE_TYPE_DEBIT_NOTE',
    'vendor_bill': 'INVOICE_TYPE_VENDOR_BILL',
    'vendor_credit_note': 'INVOICE_TYPE_VENDOR_CREDIT_NOTE',
  }
  return typeMap[frontendType] ?? ''
}

function convertToApiFrequency(frontendFreq: ResetFrequency): string {
  const freqMap: Record<ResetFrequency, string> = {
    'never': 'RESET_FREQUENCY_NEVER',
    'monthly': 'RESET_FREQUENCY_MONTHLY',
    'quarterly': 'RESET_FREQUENCY_QUARTERLY',
    'yearly': 'RESET_FREQUENCY_YEARLY',
  }
  return freqMap[frontendFreq]
}

function transformSeries(apiSeries: Record<string, unknown>): NumberingSeries {
  return {
    ...(apiSeries as unknown as NumberingSeries),
    documentType: convertInvoiceType(apiSeries.documentType as string),
    resetFrequency: convertResetFrequency(apiSeries.resetFrequency as string),
    // Handle fields that may be omitted when they have default values (0)
    currentSequence: (apiSeries.currentSequence as number) ?? 0,
    startingNumber: (apiSeries.startingNumber as number) ?? 1,
    lastResetFiscalYear: (apiSeries.lastResetFiscalYear as number) ?? 0,
    lastResetMonth: (apiSeries.lastResetMonth as number) ?? 0,
    lastResetQuarter: (apiSeries.lastResetQuarter as number) ?? 0,
  }
}

/**
 * List all numbering series for an organization and ledger
 */
export async function listNumberingSeries(
  orgId: string,
  ledgerId: string
): Promise<NumberingSeries[]> {
  try {
    const response = await fetch(
      `${API_BASE}/organizations/${orgId}/ledgers/${ledgerId}/numbering-series`,
      {
        headers: {
          'Content-Type': 'application/json',
        },
      }
    )

    if (!response.ok) {
      throw new Error(`Failed to list numbering series: ${response.statusText}`)
    }

    const data = await response.json() as { series?: Record<string, unknown>[] }
    const series = data.series ?? []
    return series.map(transformSeries)
  } catch {
    // Return empty array if API not available
    return []
  }
}

/**
 * List the series assignments (which series applies to each
 * role/category/doc-kind/draft selector) for a ledger.
 */
export async function listNumberingAssignments(
  orgId: string,
  ledgerId: string
): Promise<NumberingAssignment[]> {
  try {
    const response = await fetch(
      `${API_BASE}/organizations/${orgId}/ledgers/${ledgerId}/numbering-assignments`,
      { headers: { 'Content-Type': 'application/json' } }
    )
    if (!response.ok) {
      throw new Error(`Failed to list numbering assignments: ${response.statusText}`)
    }
    const data = await response.json() as { assignments?: NumberingAssignment[] }
    return data.assignments ?? []
  } catch {
    return []
  }
}

/**
 * Create or re-point a single series assignment. Pointing two selectors at the
 * same seriesId shares the running number across them.
 */
export async function upsertNumberingAssignment(
  orgId: string,
  ledgerId: string,
  data: UpsertNumberingAssignmentRequest
): Promise<{ success: boolean; assignment?: NumberingAssignment; error?: string }> {
  try {
    const response = await fetch(
      `${API_BASE}/organizations/${orgId}/ledgers/${ledgerId}/numbering-assignments`,
      {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data),
      }
    )
    if (!response.ok) {
      return { success: false, error: await response.text() }
    }
    return { success: true, assignment: await response.json() as NumberingAssignment }
  } catch (error) {
    return { success: false, error: String(error) }
  }
}

/**
 * Recreate any missing default numbering series for a ledger (idempotent).
 * Returns how many series were newly created.
 */
export async function restoreDefaultNumberingSeries(
  orgId: string,
  ledgerId: string
): Promise<{ success: boolean; createdCount?: number; series?: NumberingSeries[]; error?: string }> {
  try {
    const response = await fetch(
      `${API_BASE}/organizations/${orgId}/ledgers/${ledgerId}/numbering-series:restore-defaults`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: '{}',
      }
    )

    if (!response.ok) {
      return { success: false, error: await response.text() }
    }

    const data = await response.json() as { series?: Record<string, unknown>[]; createdCount?: number }
    return {
      success: true,
      createdCount: data.createdCount ?? 0,
      series: (data.series ?? []).map(transformSeries),
    }
  } catch (error) {
    return { success: false, error: String(error) }
  }
}

/**
 * Quick Setup: atomically create a full set of numbering series (draft + final,
 * per doc kind) and their assignments for a role from a single base prefix.
 * Fails with no partial writes if any computed prefix collides with an
 * existing series.
 */
export async function quickSetupRoleNumbering(
  orgId: string,
  ledgerId: string,
  data: QuickSetupRoleNumberingRequest
): Promise<{ success: boolean; data?: QuickSetupRoleNumberingResponse; error?: string }> {
  try {
    const response = await fetch(
      `${API_BASE}/organizations/${orgId}/ledgers/${ledgerId}/numbering-series:quick-setup-role`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data),
      }
    )

    if (!response.ok) {
      const error = await response.text()
      return { success: false, error }
    }

    const result = await response.json() as { series?: Record<string, unknown>[]; assignmentsCreated?: number }
    return {
      success: true,
      data: {
        series: (result.series ?? []).map(transformSeries),
        assignmentsCreated: result.assignmentsCreated ?? 0,
      },
    }
  } catch (error) {
    return { success: false, error: String(error) }
  }
}

/**
 * Create a new numbering series
 */
export async function createNumberingSeries(
  orgId: string,
  data: {
    ledgerId: string
    seriesName: string
    documentType: InvoiceType
    isDraft: boolean
    prefix: string
    numberFormat: string
    startingNumber: number
    resetFrequency: ResetFrequency
  }
): Promise<{ success: boolean; data?: NumberingSeries; error?: string }> {
  try {
    // Convert frontend types to API types
    const apiData = {
      ...data,
      documentType: convertToApiType(data.documentType),
      resetFrequency: convertToApiFrequency(data.resetFrequency),
    }

    const response = await fetch(
      `${API_BASE}/organizations/${orgId}/ledgers/${data.ledgerId}/numbering-series`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(apiData),
      }
    )

    if (!response.ok) {
      const error = await response.text()
      return { success: false, error }
    }

    const result = await response.json() as Record<string, unknown>
    return { success: true, data: transformSeries(result) }
  } catch (error) {
    return { success: false, error: String(error) }
  }
}

/**
 * Update an existing numbering series
 */
export async function updateNumberingSeries(
  orgId: string,
  seriesId: string,
  data: {
    seriesName?: string
    prefix?: string
    numberFormat?: string
    startingNumber?: number
    resetFrequency?: ResetFrequency
    isActive?: boolean
  }
): Promise<{ success: boolean; data?: NumberingSeries; error?: string }> {
  try {
    // Convert frontend types to API types
    const apiData = {
      ...data,
      ...(data.resetFrequency && { resetFrequency: convertToApiFrequency(data.resetFrequency) }),
    }

    const response = await fetch(
      `${API_BASE}/organizations/${orgId}/numbering-series/${seriesId}`,
      {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(apiData),
      }
    )

    if (!response.ok) {
      const error = await response.text()
      return { success: false, error }
    }

    const result = await response.json() as Record<string, unknown>
    return { success: true, data: transformSeries(result) }
  } catch (error) {
    return { success: false, error: String(error) }
  }
}

/**
 * Set current sequence for a numbering series
 */
export async function setCurrentSequence(
  orgId: string,
  seriesId: string,
  newSequence: number,
  reason: string
): Promise<{ success: boolean; error?: string }> {
  try {
    const response = await fetch(
      `${API_BASE}/organizations/${orgId}/numbering-series/${seriesId}/set-sequence`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ newSequence, reason }),
      }
    )

    if (!response.ok) {
      const error = await response.text()
      return { success: false, error }
    }

    return { success: true }
  } catch (error) {
    return { success: false, error: String(error) }
  }
}

/**
 * Delete a numbering series.
 *
 * By default this deactivates the series (soft delete). Pass `hardDelete` to
 * permanently remove it — the server only allows that when the series has never
 * issued a number.
 */
export async function deleteNumberingSeries(
  orgId: string,
  seriesId: string,
  hardDelete = false
): Promise<{ success: boolean; error?: string }> {
  try {
    const query = hardDelete ? '?hardDelete=true' : ''
    const response = await fetch(
      `${API_BASE}/organizations/${orgId}/numbering-series/${seriesId}${query}`,
      {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
        },
      }
    )

    if (!response.ok) {
      const error = await response.text()
      return { success: false, error }
    }

    return { success: true }
  } catch (error) {
    return { success: false, error: String(error) }
  }
}
